/**
 * Shared "is the current URL a real page?" signal.
 *
 * WHY A MODULE AND NOT A HOOK
 *
 * Two components need this answer and they sit in different branches of the
 * tree, so context is not available to both:
 *
 *   AppProvider (wrapRootElement)
 *     ├─ BrazeProvider          ← needs it, has no access to the page element
 *     └─ AppTrackingProvider
 *          └─ LayoutProvider    ← computes it, from the rendered page element
 *
 * `isProbePath` alone gets roughly half the way there — it recognises URL
 * shapes that cannot be routes (`.php`, `.aspx`, dot-segments), which covered
 * `search.php`, `space.php`, `ListInfo.php` and `DsxxJsxx.aspx` in the Aug 2026
 * sweep. The rest of that traffic (`biblio`, `scholar_lookup`,
 * `newspage/data/dtlandingsuper`) has no distinguishing shape and is only
 * identifiable by the fact that Gatsby rendered the 404 page for it — which
 * only LayoutProvider can see. Hence the published flag.
 *
 * Fails OPEN: if LayoutProvider never publishes, this reports "trackable" and
 * behaviour is exactly as before the guard existed.
 */

import {isProbePath} from './trackablePath';

let routeIsNotFound = false;
let resolved = false;
const waiters = new Set<() => void>();

/**
 * Published by LayoutProvider (from an effect) once it knows whether Gatsby
 * resolved the current URL to the 404 page.
 *
 * Also marks the route as *resolved*, which is what lets consumers wait for a
 * real answer instead of racing a timer. BrazeProvider previously guessed with
 * a 500ms delay; because the page subtree only renders once the Apollo client
 * is ready, a slow client meant the flag was still at its default when the
 * timer fired — precisely for the extension-less 404s that only this flag can
 * catch.
 */
export const setRouteIsNotFound = (value: boolean): void => {
  routeIsNotFound = value;
  resolved = true;
  // A waiter may unsubscribe itself here; Set iteration tolerates that.
  waiters.forEach(waiter => {
    try {
      waiter();
    } catch {
      /* a broken waiter must not stop the others */
    }
  });
};

/**
 * Run `callback` when the route state is known — immediately if it already is.
 * Returns an unsubscribe function.
 *
 * Note the callback may be invoked synchronously during this call, so callers
 * must not rely on the returned unsubscribe existing yet (guard with a flag).
 */
export const onRouteResolved = (callback: () => void): (() => void) => {
  waiters.add(callback);
  if (resolved) {
    try {
      callback();
    } catch {
      /* swallow */
    }
  }
  return () => {
    waiters.delete(callback);
  };
};

export const isRouteResolved = (): boolean => resolved;

/**
 * True when the current URL is a real page and analytics identity may be
 * created for it.
 *
 * Never throws — callers are on the analytics path.
 */
export const isRouteTrackable = (pathname?: string): boolean => {
  try {
    if (routeIsNotFound) return false;
    const path =
      pathname ??
      (typeof window !== 'undefined' ? window.location.pathname : '');
    return !isProbePath(path);
  } catch {
    // Fail open — never suppress tracking because of a bug in this guard.
    return true;
  }
};

/** Test-only. */
export const __resetRouteState = (): void => {
  routeIsNotFound = false;
  resolved = false;
  waiters.clear();
};
